/**
 * 브랜드 자산 — ★ **사용자가 준 실제 로고**다(2026-10-04).
 *
 * 원본 시트에서 픽셀로 잰 경계로 잘라냈다(`web/public/brand/`):
 *   심볼     1536x1024 시트의 x 469..1069 · y 87..664
 *   워드마크 같은 시트의 x 552..1279 · y 778..939
 *
 * ⚠️ 원본은 **배경이 완전 투명이고 가장자리가 선명하다**(알파 0→251 이 2px).
 *   미리보기가 뿌옇게 보이는 것은 뷰어 탓이지 아트 탓이 아니다 — 실측했다.
 *
 * ⚠️ 워드마크는 **흰색판과 검은색판 둘**이다. LENS 는 다크 우선이라 기본은
 *   흰색이고, 밝은 판(`[data-theme='light']`)에서는 검은색이 나와야 한다.
 *   ★ CSS 한 줄로 가르지 못해(이미지라) 두 장을 겹쳐 두고 테마로 숨긴다.
 */
export function LensSymbol({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <img
      src="/brand/lens-symbol.png"
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      className={className}
      style={{ width: size, height: size }}
    />
  );
}

/**
 * 워드마크. ⚠️ 가로세로비 728:162 를 지킨다 — 높이만 주면 너비가 따라온다.
 *
 * ★ `alt` 를 비우지 않는다 — 이것이 제품 이름이라 스크린리더가 읽어야 한다.
 *
 * ⚠️⚠️ **`tone` 은 테마가 아니라 「무엇 위에 놓이는가」다.**
 *
 * 처음에는 `[data-theme]` 만 보고 흰판/검은판을 갈랐다. ★ 그런데
 * **사이드바는 라이트 모드에서도 어두운 채로 남는다**(`--lens-sidebar-surface`
 * 에 라이트 변형이 없다 — 원본 앱도 그랬다). ⚠️ 그래서 라이트로 바꾸는 순간
 * **어두운 사이드바 위에 검은 워드마크**가 찍혀 거의 사라졌다(2026-10-04 실측).
 *
 * ★ 테마는 **면의 밝기를 알려주지 않는다.** 그래서 호출부가 말한다.
 *   `onDark`  — 늘 어두운 면(사이드바) · **흰판 고정**
 *   `surface` — 테마를 따르는 면(카드·본문) · **테마로 가른다**
 */
export function LensWordmark({
  height = 22,
  tone = 'surface',
}: {
  height?: number;
  tone?: 'onDark' | 'surface';
}) {
  const width = Math.round((728 / 162) * height);
  const common = { alt: 'LENS', width, height, style: { width, height } } as const;

  // ★ 늘 어두운 면 — 한 장만 그린다. ⚠️ 겹쳐 두면 테마가 바뀔 때 숨는다.
  if (tone === 'onDark') {
    return <img src="/brand/lens-wordmark-light.png" {...common} className="block" />;
  }

  return (
    <span className="relative inline-flex" style={{ width, height }}>
      {/* 다크(기본) */}
      <img src="/brand/lens-wordmark-light.png" {...common} className="block dark-only" />
      {/* 라이트 */}
      <img src="/brand/lens-wordmark-dark.png" {...common} className="absolute inset-0 light-only" />
    </span>
  );
}
